// Admin-tab "IT-overzicht": per-gebruiker widget/mobile-versies, per-PC
// versiehistorie en widget-logs — het scherm dat een ziekenhuis-IT-beheerder
// daadwerkelijk gebruikt om een collega met een oude/vastgelopen widget te
// helpen. Zelfde opzet en klassen als AdminClientVersionsTab.tsx hiernaast.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, authFetchJson } from "@/lib/api";
import { dateFromNsTimestamp, formatDateTime } from "@/lib/format";
import { Pagination } from "@/components/Pagination";

type ITOverviewUser = {
  user_id: string;
  email: string;
  full_name: string;
  widget_version: string | null;
  widget_hostname: string | null;
  widget_platform: string | null;
  widget_last_seen_at: string | null;
  mobile_version: string | null;
  mobile_platform: string | null;
  mobile_last_seen_at: string | null;
};

type VersionRow = {
  version: string;
  platform: string | null;
  hostname: string | null;
  first_seen_at: string;
  last_seen_at: string;
};

type LogLine = { ts: string; line: string };

const dash = (v: string | null | undefined): string => v ?? "—";

const LOG_HOUR_PRESETS = [24, 72, 168] as const;

export function AdminITOverviewTab() {
  const [items, setItems] = useState<ITOverviewUser[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, VersionRow[]>>({});

  const [logUser, setLogUser] = useState<ITOverviewUser | null>(null);
  const [logHours, setLogHours] = useState<number>(24);
  // "" = alle pc's van deze gebruiker (geen hostname-filter naar de API).
  const [logHostname, setLogHostname] = useState<string>("");
  const [logFilter, setLogFilter] = useState("");
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [logError, setLogError] = useState("");
  const [logLoading, setLogLoading] = useState(false);

  // Volgordebewaking: elke `load()`/`loadLogs()`-aanroep claimt een oplopend
  // sequencenummer. Als er een tragere, oudere aanvraag terugkomt nadat een
  // nieuwere al is gestart, wijkt haar sequencenummer af van het huidige en
  // wordt haar resultaat genegeerd — dat voorkomt dat een stale response een
  // nieuwer resultaat overschrijft (bijv. bij snel typen in het zoekveld, of
  // snel na elkaar "Logs" voor twee verschillende gebruikers openen).
  const loadSeq = useRef(0);
  const logSeq = useRef(0);
  // loadLogs moet z'n resultaat ook laten vallen als de admin ondertussen
  // een andere gebruiker heeft gekozen, ook al is dat toevallig de nieuwste
  // aanvraag in volgorde — vandaar de expliciete user-id-check naast het
  // sequencenummer.
  const logUserIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const seq = (loadSeq.current += 1);
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (query.trim()) params.set("q", query.trim());
      const res = await authFetchJson<{ items: ITOverviewUser[]; total: number }>(
        `/api/admin/it-overview/users?${params.toString()}`,
      );
      if (loadSeq.current !== seq) return;
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      if (loadSeq.current !== seq) return;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setError("Geen admin-rechten.");
      } else {
        setError(err instanceof Error ? err.message : "Fout bij laden.");
      }
    } finally {
      if (loadSeq.current === seq) setLoading(false);
    }
  }, [query, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  // Gedeeld door de historie-uitklapper én de log-viewer: beide hebben de
  // per-PC widget-versierijen van deze gebruiker nodig (hostnames voor de
  // log-viewer, volledige rijen voor de historietabel).
  const fetchVersions = useCallback(
    async (userId: string) => {
      if (history[userId]) return history[userId];
      try {
        const res = await authFetchJson<{ widget: VersionRow[]; mobile: VersionRow[] }>(
          `/api/admin/it-overview/users/${encodeURIComponent(userId)}/versions`,
        );
        const widget = res.widget ?? [];
        setHistory((prev) => ({ ...prev, [userId]: widget }));
        return widget;
      } catch {
        setHistory((prev) => ({ ...prev, [userId]: [] }));
        return [];
      }
    },
    [history],
  );

  const toggleHistory = useCallback(
    async (userId: string) => {
      setExpanded((prev) => (prev === userId ? null : userId));
      await fetchVersions(userId);
    },
    [fetchVersions],
  );

  const loadLogs = useCallback(async (target: ITOverviewUser, hours: number, hostname: string) => {
    const seq = (logSeq.current += 1);
    setLogLoading(true);
    setLogError("");
    setLogLines([]);
    try {
      const params = new URLSearchParams({ hours: String(hours), limit: "500" });
      if (hostname) params.set("hostname", hostname);
      const res = await authFetchJson<{ lines: LogLine[] }>(
        `/api/admin/it-overview/users/${encodeURIComponent(target.user_id)}/logs?${params.toString()}`,
      );
      if (logSeq.current !== seq || logUserIdRef.current !== target.user_id) return;
      setLogLines(res.lines ?? []);
    } catch (err) {
      if (logSeq.current !== seq || logUserIdRef.current !== target.user_id) return;
      setLogError(
        err instanceof ApiError && err.status === 503
          ? "Logdienst tijdelijk niet beschikbaar."
          : "Logs laden mislukt.",
      );
    } finally {
      if (logSeq.current === seq && logUserIdRef.current === target.user_id) setLogLoading(false);
    }
  }, []);

  // De API levert nieuwste-eerst (nodig om bij een cap de meest recente
  // regels te pakken); voor leesbaarheid — vooral meerregelige stack traces —
  // tonen we chronologisch, oudste eerst. Alleen voor weergave omgedraaid.
  const visibleLogLines = useMemo(() => {
    const filtered = logFilter.trim()
      ? logLines.filter((l) => l.line.toLowerCase().includes(logFilter.trim().toLowerCase()))
      : logLines;
    return [...filtered].reverse();
  }, [logLines, logFilter]);

  const logHostnameOptions = useMemo(() => {
    if (!logUser) return [];
    const rows = history[logUser.user_id] ?? [];
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const row of rows) {
      if (row.hostname && !seen.has(row.hostname)) {
        seen.add(row.hostname);
        ordered.push(row.hostname);
      }
    }
    return ordered;
  }, [history, logUser]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-[var(--cs-ink-muted)]">
        Widget- en mobiel-versies per gebruiker, met de laatste tien
        versiewissels per computer (hostname) en de widget-logs van die
        computer.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Filter op e-mailadres of naam"
          className="flex-1 rounded-lg border border-[rgba(10,52,96,0.15)] px-3 py-1.5 text-sm"
        />
        <span className="text-xs text-[var(--cs-ink-muted)]">{total} gebruikers</span>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p> : null}

      {!loading && items.length === 0 && !error ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">
          Nog geen versiemeldingen voor deze organisatie.
        </p>
      ) : null}

      {items.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-[rgba(10,52,96,0.10)] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(10,52,96,0.08)] text-left text-xs uppercase tracking-wide text-[var(--cs-ink-muted)]">
                <th className="px-4 py-3">Gebruiker</th>
                <th className="px-4 py-3">Widget</th>
                <th className="px-4 py-3">Hostname</th>
                <th className="px-4 py-3">Laatst gezien (widget)</th>
                <th className="px-4 py-3">Mobiel</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Fragment key={item.user_id}>
                  <tr className="border-b border-[rgba(10,52,96,0.06)] align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[var(--cs-navy)]">{item.email}</div>
                      <div className="text-xs text-[var(--cs-ink-muted)]">
                        {item.full_name || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">{dash(item.widget_version)}</td>
                    <td className="px-4 py-3">{dash(item.widget_hostname)}</td>
                    <td className="px-4 py-3">{formatDateTime(item.widget_last_seen_at)}</td>
                    <td className="px-4 py-3">
                      {item.mobile_version ? (
                        <>
                          {item.mobile_version} ({dash(item.mobile_platform)})
                          <div className="text-xs text-[var(--cs-ink-muted)]">
                            {formatDateTime(item.mobile_last_seen_at)}
                          </div>
                        </>
                      ) : (
                        <span className="text-[var(--cs-ink-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => void toggleHistory(item.user_id)}
                          className="text-xs font-medium text-[var(--cs-navy)] underline"
                        >
                          {expanded === item.user_id ? "Verberg historie" : "Historie"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            logUserIdRef.current = item.user_id;
                            setLogUser(item);
                            setLogFilter("");
                            setLogHours(24);
                            setLogHostname("");
                            void fetchVersions(item.user_id);
                            void loadLogs(item, 24, "");
                          }}
                          className="text-xs font-medium text-[var(--cs-navy)] underline"
                        >
                          Logs
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === item.user_id ? (
                    <tr>
                      <td colSpan={6} className="bg-[var(--cs-ivory)]/80 px-6 py-3">
                        {(history[item.user_id] ?? []).length === 0 ? (
                          <span className="text-xs text-[var(--cs-ink-muted)]">
                            Geen widget-historie.
                          </span>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[var(--cs-ink-muted)]">
                                <th className="py-1 pr-4">Versie</th>
                                <th className="py-1 pr-4">Hostname</th>
                                <th className="py-1 pr-4">Platform</th>
                                <th className="py-1 pr-4">Eerst gezien</th>
                                <th className="py-1">Laatst gezien</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(history[item.user_id] ?? []).map((row) => (
                                <tr key={`${row.version}-${row.hostname ?? ""}-${row.first_seen_at}`}>
                                  <td className="py-1 pr-4">{row.version}</td>
                                  <td className="py-1 pr-4">{dash(row.hostname)}</td>
                                  <td className="py-1 pr-4">{dash(row.platform)}</td>
                                  <td className="py-1 pr-4">{formatDateTime(row.first_seen_at)}</td>
                                  <td className="py-1">{formatDateTime(row.last_seen_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {total > pageSize ? (
        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      ) : null}

      {logUser ? (
        <div className="rounded-xl border border-[rgba(10,52,96,0.10)] bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="mr-auto text-sm font-semibold text-[var(--cs-navy)]">
              Widget-logs — {logUser.email}
              {logHostname ? ` (${logHostname})` : " (alle pc's)"}
            </h3>
            <select
              value={logHostname}
              onChange={(e) => {
                const hostname = e.target.value;
                setLogHostname(hostname);
                void loadLogs(logUser, logHours, hostname);
              }}
              className="rounded-lg border border-[rgba(10,52,96,0.15)] px-2.5 py-1 text-xs text-[var(--cs-navy)]"
            >
              <option value="">Alle pc's</option>
              {logHostnameOptions.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            {LOG_HOUR_PRESETS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => {
                  setLogHours(h);
                  void loadLogs(logUser, h, logHostname);
                }}
                className={
                  logHours === h
                    ? "rounded-lg bg-[var(--cs-navy)] px-2.5 py-1 text-xs text-white"
                    : "rounded-lg border border-[rgba(10,52,96,0.15)] px-2.5 py-1 text-xs text-[var(--cs-navy)]"
                }
              >
                {h} u
              </button>
            ))}
            <input
              type="search"
              value={logFilter}
              onChange={(e) => setLogFilter(e.target.value)}
              placeholder="Filter in logregels"
              className="rounded-lg border border-[rgba(10,52,96,0.15)] px-2.5 py-1 text-xs"
            />
            <button
              type="button"
              onClick={() => {
                logUserIdRef.current = null;
                setLogUser(null);
                setLogHostname("");
              }}
              className="text-xs font-medium text-[var(--cs-navy)] underline"
            >
              Sluiten
            </button>
          </div>

          {logError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {logError}
            </div>
          ) : logLoading ? (
            <p className="text-sm text-[var(--cs-ink-muted)]">Logs laden…</p>
          ) : visibleLogLines.length === 0 ? (
            <p className="text-sm text-[var(--cs-ink-muted)]">Geen logregels in dit venster.</p>
          ) : (
            <pre className="max-h-96 overflow-auto rounded-lg bg-[var(--cs-ivory)] p-3 text-xs leading-5">
              {visibleLogLines
                .map(
                  (l) =>
                    `[${formatDateTime(dateFromNsTimestamp(l.ts).toISOString(), { seconds: true })}] ${l.line}`,
                )
                .join("\n")}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}
